import * as Sentry from '@sentry/vue';
import { sentryComponentName } from '@/utils/constants/sentryConstants';

const allowedComponents = Object.values(sentryComponentName);

/**
 * 안전하게 에러 메시지를 추출하는 유틸
 * @param {any} err
 * @returns {string}
 */
export function safeGetErrorMessage(err) {
  if (!err) return '에러 객체 없음';
  if (typeof err === 'string') return err;
  if (typeof err.message === 'string') return err.message;
  if ('message' in err) return String(err.message);
  return '에러 메시지 없음';
}

/**
 * Sentry에 스크립트 에러를 전송하는 공통 함수
 * @param {Object} options
 * @param {Error|any} options.err - 실제 에러 객체
 * @param {String} options.componentName - 컴포넌트 이름
 * @param {String} [options.alertChannel] - Slack 연동을 위한 알림 태그
 * @param {String} [options.hookName] - mounted, created 등 위치 정보
 * @param {Object} [options.props] - 컴포넌트 props (옵션)
 * @param {String} [options.info] - Vue의 info 값 (옵션)
 * @param {Boolean} [options.isProd] - prod 환경 여부
 */
export function reportComponentError({
  err,
  componentName,
  alertChannel = '',
  hookName = '',
  props = {},
  info = '',
  isProd = false,
  learnId = '',
}) {
  const safeMessage = safeGetErrorMessage(err);
  const msg = `[SCRIPT ERROR] 컴포넌트: ${componentName} 메시지: ${safeMessage}`;

  try {
    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'vue_script');
      scope.setTag('vue_component', componentName);

      if (learnId) scope.setTag('learn_id', learnId); // learnId 태그 추가

      if (info) scope.setExtra('info', info);
      if (hookName) scope.setExtra('hook', hookName);
      if (props) scope.setContext('props', props);

      if (isProd && alertChannel) {
        scope.setTag('slack_channel', alertChannel);
      }

      Sentry.captureException(new Error(msg));
    });
  } catch (e) {
    console.error('Sentry 전송 중 오류:', e);
  }
}

/**
 * Vue 전역 에러 핸들러 등록 함수
 * @param {VueConstructor} Vue
 * @param {String} alertChannel
 */
export function captureScriptError(Vue, alertChannel) {
  try {
    Vue.config.errorHandler = (err, vm, info) => {
      const componentName = vm.$options.name || 'anonymous';

      if (allowedComponents.includes(componentName)) {
        reportComponentError({
          err,
          componentName,
          alertChannel,
          info,
          props: vm.$props || {},
          isProd: process.env.VUE_APP_SENTRY_PHASE === 'VD_PROD',
        });

        // 디버깅 로그
        console.group('Vue Config Error Handler Group');
        console.log('VUE Component :', componentName);
        console.log('Error info :', info);
        console.log('Props :', vm.$props);
        console.log('CONFIG ERROR HANDLER :', err);
        console.groupEnd();
      }
    };
  } catch (error) {
    console.error('Sentry Vue ErrorHandler 등록 중 에러 발생:', error);
  }
}

/**
 * Sentry에 일반 상태 로그를 전송하는 함수 (에러 아님)
 * @param {Object} options
 * @param {String} options.message - 로그에 남길 메시지
 * @param {String} options.componentName - 컴포넌트 이름
 * @param {String} [options.alertChannel] - Slack 태그 (옵션)
 * @param {String} [options.hookName] - mounted, created 등 위치 정보
 * @param {Object} [options.props] - 컴포넌트 props (옵션)
 * @param {Boolean} [options.isProd] - prod 환경 여부
 */
export function reportComponentLog({
  message = '',
  componentName = '',
  alertChannel = '',
  hookName = '',
  props = {},
  isProd = process.env.VUE_APP_SENTRY_PHASE === 'VD_PROD',
  learnID = '', // 추가
}) {
  const logMessage = `[SCRIPT LOG (정보성 로그)] 컴포넌트: ${componentName} 메시지: ${message} time: ${new Date().toISOString()}`;

  try {
    Sentry.withScope((scope) => {
      scope.setTag('log_type', 'vue_script_log');
      scope.setTag('vue_component', componentName);
      if (learnID) scope.setTag('learn_id', learnID); // learnID 태그 추가
      if (hookName) scope.setExtra('hook', hookName);
      if (props) scope.setContext('props', props);

      // 핑거프린트 설정: new Date로 완전히 유니크하게 설정
      const uniqueTimestamp = new Date().toISOString();
      scope.setFingerprint([
        'vue_script_log',
        componentName,
        message,
        hookName || 'no_hook',
        uniqueTimestamp,
      ]);

      // Slack 알림 없이 로그만 전송 (info 수준)
      Sentry.captureMessage(logMessage, alertChannel === 'monitoring' ? 'error' : 'info');
    });
  } catch (e) {
    console.error('Sentry 로그 전송 중 오류:', e);
  }
}